import { z } from 'zod';

// src/grouping/model.ts
var GROUPING_COMPARATORS = [
  "eq",
  "neq",
  "in",
  "nin",
  "contains",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "missing"
];
var GROUPING_SCOPES = ["default", "org", "tenant", "user"];
var conditionSchema = z.object({
  /** Field path on a {@link TxFact}, e.g. `method`, `valueUsd`, `tags`. */
  field: z.string().trim().min(1),
  op: z.enum(GROUPING_COMPARATORS),
  /** Comparison operand. Omitted for `exists`/`missing`. */
  value: z.unknown().optional()
});
var stepSchema = z.object({
  id: z.string().trim().min(1),
  label: z.string().trim().optional(),
  /** All conditions must hold (AND). An empty list matches any transaction. */
  match: z.array(conditionSchema).default([])
});
var correlationSchema = z.object({
  /** Fields whose values must be equal across every matched tx in a group. */
  equalFields: z.array(z.string().trim().min(1)).optional()
});
var GroupingRuleSchema = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1, "Name is required").max(120),
  description: z.string().trim().max(2e3).optional(),
  enabled: z.boolean().default(true),
  scope: z.enum(GROUPING_SCOPES).default("user"),
  /** Ordered steps; a group must satisfy them in sequence. */
  steps: z.array(stepSchema).min(1, "At least one step is required"),
  correlation: correlationSchema.optional(),
  /** Max elapsed time (ms) between the first and last step. 0/omitted = unbounded. */
  windowMs: z.number().int().nonnegative().optional(),
  /** Require the matched steps to span more than one chain. */
  crossChain: z.boolean().default(false),
  /** Optional colour for the group badge/edge in the graph. */
  color: z.string().trim().optional()
});
function validateGroupingRule(rule) {
  const result = GroupingRuleSchema.safeParse(rule);
  if (result.success) return { success: true, data: result.data, fieldErrors: {} };
  const flat = result.error.flatten();
  return {
    success: false,
    fieldErrors: flat.fieldErrors,
    message: flat.formErrors[0]
  };
}
function approveThenSendRule(overrides = {}) {
  return GroupingRuleSchema.parse({
    id: overrides.id ?? "approve-then-send",
    name: overrides.name ?? "Approve then send",
    description: overrides.description ?? "An approval followed by a transfer from the same address \u2014 the classic two-step settlement pattern.",
    crossChain: overrides.crossChain ?? true,
    correlation: overrides.correlation ?? { equalFields: ["from"] },
    windowMs: overrides.windowMs,
    steps: overrides.steps ?? [
      { id: "approve", label: "Approve", match: [{ field: "method", op: "eq", value: "approve" }] },
      {
        id: "send",
        label: "Send",
        match: [{ field: "method", op: "in", value: ["transfer", "transferFrom", "send"] }]
      }
    ],
    ...overrides
  });
}

// src/grouping/evaluate.ts
function fieldValue(tx, field) {
  if (field in tx) return tx[field];
  if (field.includes(".")) {
    let cur = tx;
    for (const part of field.split(".")) {
      if (cur == null || typeof cur !== "object") return void 0;
      cur = cur[part];
    }
    return cur;
  }
  return void 0;
}
function asNumber(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
function compare(actual, op, expected) {
  switch (op) {
    case "exists":
      return actual != null;
    case "missing":
      return actual == null;
    case "eq":
      return actual === expected;
    case "neq":
      return actual !== expected;
    case "in":
      return Array.isArray(expected) && expected.includes(actual);
    case "nin":
      return Array.isArray(expected) && !expected.includes(actual);
    case "contains": {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === "string") return actual.includes(String(expected));
      return false;
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = asNumber(actual);
      const b = asNumber(expected);
      if (a === void 0 || b === void 0) return false;
      return op === "gt" ? a > b : op === "gte" ? a >= b : op === "lt" ? a < b : a <= b;
    }
    default:
      return false;
  }
}
function conditionHolds(tx, condition) {
  return compare(fieldValue(tx, condition.field), condition.op, condition.value);
}
function stepMatches(tx, step) {
  return step.match.every((c) => conditionHolds(tx, c));
}
function correlationKey(tx, equalFields) {
  if (!equalFields || equalFields.length === 0) return "";
  return equalFields.map((f) => `${f}=${String(fieldValue(tx, f) ?? "")}`).join("&");
}
function evaluateRule(rule, txs) {
  if (rule.enabled === false || rule.steps.length === 0) return [];
  const equalFields = rule.correlation?.equalFields;
  const partitions = /* @__PURE__ */ new Map();
  for (const tx of txs) {
    const key = correlationKey(tx, equalFields);
    let bucket = partitions.get(key);
    if (!bucket) {
      bucket = [];
      partitions.set(key, bucket);
    }
    bucket.push(tx);
  }
  const groups = [];
  for (const [key, partTxs] of partitions) {
    const sorted = [...partTxs].sort((a, b) => a.timestamp - b.timestamp);
    let current = [];
    let stepIdx = 0;
    const reset = () => {
      current = [];
      stepIdx = 0;
    };
    for (const tx of sorted) {
      if (current.length > 0 && rule.windowMs && rule.windowMs > 0) {
        const anchor = current[0].tx.timestamp;
        if (tx.timestamp - anchor > rule.windowMs) {
          reset();
        }
      }
      const step = rule.steps[stepIdx];
      if (stepMatches(tx, step)) {
        current.push({ stepId: step.id, tx });
        stepIdx += 1;
        if (stepIdx === rule.steps.length) {
          const group = finalizeGroup(rule, key, current);
          if (group) groups.push(group);
          reset();
        }
      } else if (stepIdx > 0 && stepMatches(tx, rule.steps[0])) {
        current = [{ stepId: rule.steps[0].id, tx }];
        stepIdx = 1;
      }
    }
  }
  return groups;
}
function finalizeGroup(rule, key, matches) {
  const chainIds = [...new Set(matches.map((m) => m.tx.chainId))];
  if (rule.crossChain && chainIds.length < 2) return null;
  const times = matches.map((m) => m.tx.timestamp);
  const spanMs = Math.max(...times) - Math.min(...times);
  return { ruleId: rule.id, key, matches, chainIds, spanMs };
}
function evaluateRules(rules, txs) {
  return rules.flatMap((rule) => evaluateRule(rule, txs));
}

// src/grouping/roles.ts
var CANON_SOURCE = "source";
var CANON_DESTINATION = "destination";
var DEFAULT_COMPLETE_ROLES = [CANON_SOURCE, CANON_DESTINATION];
var SOURCE_SYNONYMS = /* @__PURE__ */ new Set(["source", "send", "origin"]);
var DESTINATION_SYNONYMS = /* @__PURE__ */ new Set(["destination", "receive", "dest"]);
var ROLE_RANK = {
  source: 0,
  send: 0,
  origin: 0,
  destination: 1,
  receive: 1,
  dest: 1
};
var UNKNOWN_ROLE_RANK = 2;
var ROLE_COMPLEMENT = {
  [CANON_SOURCE]: CANON_DESTINATION,
  [CANON_DESTINATION]: CANON_SOURCE
};
var OPEN_ENDED_SAGA_KINDS = /* @__PURE__ */ new Set([
  "trade.token_position",
  "pool.lifecycle",
  "token.launch",
  // A wallet goes on transferring to a venue, so the backend registers this
  // saga with `aggregating_completion` and pins no completion set. Left out
  // here, its group would be read as forever awaiting a counterpart, and every
  // exchange-transfer card would carry a badge saying a leg was missing.
  "flow.exchange_deposit"
]);
var OPEN_ENDED_SAGA_PREFIXES = ["trade."];
var OPEN_ENDED_RELATIONSHIP_TYPES = /* @__PURE__ */ new Set([
  "trade",
  // An authored address-flow group: its saga kind is the tenant's own string, so
  // no list above can name it, and every one of them is open-ended.
  "address_flow"
]);
function canonRole(role) {
  const value = (role ?? "").trim().toLowerCase();
  if (SOURCE_SYNONYMS.has(value)) return CANON_SOURCE;
  if (DESTINATION_SYNONYMS.has(value)) return CANON_DESTINATION;
  return value;
}
function roleRank(role) {
  return ROLE_RANK[(role ?? "").trim().toLowerCase()] ?? UNKNOWN_ROLE_RANK;
}
var ROLE_LABELS = {
  // A token's launch — `commons.grouping.token_launch`.
  token_deploy: "Token deployed",
  // A pool's lifecycle — `commons.grouping.pools`. Shared with the launch above.
  pool_open: "Pool created",
  pool_priced: "Pool priced",
  liquidity_add: "Liquidity added",
  liquidity_remove: "Liquidity removed",
  liquidity_move: "Liquidity changed",
  swap: "Traded",
  // A finding re-files its members under what the finding says they did.
  liquidity_pull: "Liquidity pulled",
  insider_buy: "Insider bought",
  // A JIT bracket (`pool.jit_front_run`). Deliberately not the sandwich's
  // `frontrun`/`backrun` wording, which the backend keeps for `mev.sandwich`
  // alone: those legs move the price and take the difference out of the trade
  // between them, whereas these add real depth for its duration.
  jit_open: "Liquidity added just in time",
  jit_close: "Liquidity pulled straight back",
  // The trade the bracket was built around. NOT "victim": the added depth is real
  // while this trade executes, so it slips LESS than the pool alone would have
  // made it. The party that pays is the pool's passive LPs, diluted out of the
  // fee on this flow — and they are positions, so no group member is them.
  target_swap: "Trade the liquidity was placed around",
  // Custody — `commons.grouping.exchange_flow`. Direction is the whole meaning
  // of these two, and the prettifier flattens it: "Exchange Deposit" and
  // "Exchange Withdrawal" are noun phrases that leave the reader to work out
  // which way the money went and whose it was.
  exchange_deposit: "Sent to the exchange",
  exchange_withdrawal: "Received from the exchange",
  // A forwarding chain. Written as "as far back as we could see" rather than
  // "source", which is the claim the detector cannot make: the walk stops at
  // its depth bound or at the first hop nobody ingested, so this wallet may
  // well have a funder of its own.
  chain_origin: "Earliest hop we can see",
  chain_hop: "Passed the funds on",
  chain_deposit: "Reached the exchange"
};
function roleLabel(role) {
  const canon = canonRole(role);
  if (!canon) return "";
  const known = ROLE_LABELS[canon];
  if (known) return known;
  const words = canon.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function isOpenEndedGroup(sagaKind, relationshipType, openEnded) {
  if (openEnded === true) return true;
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (OPEN_ENDED_SAGA_KINDS.has(kind)) return true;
  if (OPEN_ENDED_SAGA_PREFIXES.some((prefix) => kind.startsWith(prefix))) return true;
  return OPEN_ENDED_RELATIONSHIP_TYPES.has((relationshipType ?? "").trim().toLowerCase());
}
function presentRoles(roles) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const role of roles) {
    const value = canonRole(role);
    if (value && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}
function expectedMissingRoles(rolesPresent, completeRoles) {
  const present = rolesPresent.filter((r) => r).map((r) => canonRole(r));
  const presentSet = new Set(present.filter((r) => r));
  let candidates = [];
  if (completeRoles && completeRoles.length > 0) {
    candidates = completeRoles.filter((r) => r).map((r) => canonRole(r));
  } else {
    for (const role of presentSet) {
      const complement = ROLE_COMPLEMENT[role];
      if (complement) candidates.push(complement);
    }
    if (candidates.length === 0) candidates = [...DEFAULT_COMPLETE_ROLES];
  }
  const seen = /* @__PURE__ */ new Set();
  const expected = [];
  for (const role of [...present, ...candidates]) {
    if (role && !seen.has(role)) {
      seen.add(role);
      expected.push(role);
    }
  }
  return { expected, missing: expected.filter((role) => !presentSet.has(role)) };
}

// src/grouping/sagas.ts
var SAGA_LABELS = {
  "bridge.ccip": "Chainlink CCIP",
  "bridge.cctp": "Circle CCTP",
  "bridge.wormhole": "Wormhole",
  // The rest of the bundled bridges, which fell through to the generic
  // prettify and so introduced themselves as "Bridge Layerzero V2" and
  // "Bridge Ibc" — the namespace leaked into the name and the protocol's own
  // capitalisation was lost. A card naming a protocol should spell it the way
  // the protocol does.
  //
  // Both LayerZero generations are listed and both say which: they are
  // different rails with different contracts and different chain numbering,
  // and a group card that called them both "LayerZero" would leave a reader
  // unable to tell which index to go and look in.
  "bridge.layerzero_v1": "LayerZero V1",
  "bridge.layerzero_v2": "LayerZero V2",
  "bridge.across_v3": "Across",
  "bridge.hyperlane": "Hyperlane",
  "bridge.mayan": "Mayan",
  // The one saga in this list whose key is not on the chain. A Relay fill is a
  // solver paying out of its own capital and identifies itself to nobody, so the
  // two legs are joined by the request id Relay's own API mints — carried as a
  // `CrossChainRef` with `off_chain` provenance. Nothing else here needs to know
  // that; the label is the only Relay-specific line in this package.
  "bridge.relay": "Relay",
  "bridge.hyperliquid": "Hyperliquid bridge",
  "bridge.hop": "Hop",
  "bridge.ibc": "IBC",
  "trade.token_position": "Trading position",
  "token.launch": "Token launch",
  "pool.lifecycle": "Pool lifecycle",
  // A prediction market's whole story, keyed on its `conditionId`. "Prediction
  // market" rather than "Conditional tokens" — the latter is the name of the
  // contract standard, and a reader looking at a group of bets on a question
  // should not have to know it.
  "market.lifecycle": "Prediction market",
  "pool.rug": "Rug pull",
  "pool.jit_front_run": "JIT front-run",
  "mev.sandwich": "Sandwich",
  "launch.insider_buy": "Insider buy",
  // Custody. "Exchange transfers" rather than "Exchange deposits" because the
  // group holds both directions — the saga kind names the common case, the
  // group does not.
  "flow.exchange_deposit": "Exchange transfers",
  // One CoW order's placement and the settlement that filled it, keyed on the
  // derived `orderUid`. Prettified it read "Exchange Cowswap Order".
  "exchange.cowswap_order": "CoW Swap order",
  // NOT "Peel chain", which is the technique's name among investigators and
  // means nothing to anybody else. What the finding says is that money moved
  // through a series of wallets and stopped somewhere it can no longer be
  // followed.
  //
  // "somewhere" rather than "an exchange" because the detector's terminus is a
  // CLASS now — an exchange, a custodial service, a bridge escrow, a mixer, a
  // sanctioned address. A static label naming one of them would be wrong for
  // the other four; `peelChainLabel` below narrows it when the group's payload
  // says which.
  "flow.peel_chain": "Funds forwarded through intermediaries",
  // NOT "Dust campaign", for the same reason the peel chain is not called one:
  // "dusting" is the technique's name among investigators and says nothing to
  // the person whose wallet is full of it. What the finding says is that one
  // address sprayed worthless transfers at a great many wallets.
  "spam.dust_campaign": "Bulk unsolicited transfers",
  approve_spend: "Approve / spend",
  userop_retry: "User-op retry",
  // Every transaction touching one catalog entity — a set of wallets someone
  // labelled. The served group name is "Entity activity · <entity name>"
  // (`commons.grouping.naming`), and a commons test fails if this map and that
  // one spell a family differently.
  "entity.flow": "Entity activity"
};
var GROUP_RELATIONSHIP_LABELS = {
  cross_chain_message: "Cross-chain",
  intent_fulfillment: "Cross-chain",
  approve_spend: "Approve / spend",
  userop_retry: "Retry",
  entity_flow: "Entity flow",
  trade: "Position",
  pool_lifecycle: "Pool activity",
  token_launch: "Token launch",
  market_lifecycle: "Market activity",
  exchange_deposit: "Exchange flow",
  address_flow: "Address activity",
  // The finding categories. They were bare strings on the detector's writer
  // until the enum learned them, so every one of them landed on the "Linked"
  // fallback below — which reads as "we found a relationship but can't say
  // what", about the groups that exist precisely because something was named.
  jit_liquidity: "JIT liquidity",
  mev_sandwich: "Sandwich",
  liquidity_rug: "Rug pull",
  insider_buy: "Insider buy",
  peel_chain: "Forwarded through wallets",
  custom: "Linked"
};
function groupRelationshipLabel(relationshipType) {
  const key = (relationshipType ?? "").trim().toLowerCase();
  return GROUP_RELATIONSHIP_LABELS[key] ?? "Linked";
}
var FINDING_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.rug",
  "pool.jit_front_run",
  "mev.sandwich",
  "launch.insider_buy",
  // The `flow.` namespace splits the same way `pool.` does: a wallet's
  // transfers to an exchange are ordinary activity, while funds arriving there
  // through a run of intermediate wallets is a detector's claim about them.
  "flow.peel_chain"
  // NOTE: `spam.dust_campaign` is deliberately NOT here, though a detector is
  // what recognises it and `tx_grouping.config.policy.FINDING_SAGA_KINDS` does
  // list it. That set decides SCOPE; this one decides how the group renders,
  // and a finding card enumerates the legs that make the case — three for a
  // sandwich, four for a peel chain. A campaign has forty thousand. It is in
  // ACTIVITY_SAGA_KINDS, matching `commons/grouping/shapes.py`.
]);
var TERMINUS_PHRASE = {
  exchange: "an exchange",
  cefi: "a custodial service",
  bridge: "a bridge",
  mixer: "a mixer",
  sanctioned: "a sanctioned address"
};
function peelChainLabel(terminusClass) {
  const phrase = TERMINUS_PHRASE[(terminusClass ?? "").trim().toLowerCase()];
  if (phrase) return `Funds forwarded to ${phrase}`;
  return SAGA_LABELS["flow.peel_chain"] ?? "Funds forwarded through intermediaries";
}
function isFindingSaga(kind) {
  return FINDING_SAGA_KINDS.has((kind ?? "").trim().toLowerCase());
}
var POSITION_SAGA_KINDS = /* @__PURE__ */ new Set(["trade.token_position"]);
var ACTIVITY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.lifecycle",
  "token.launch",
  // A prediction market's history: its creation, every position opened and
  // closed on it, its resolution and the payouts after. Open-ended over one
  // subject exactly like a pool, and capped the same way.
  "market.lifecycle",
  // One address's bulk dusting — the shape this set exists for, taken to its
  // extreme. A campaign's members number in the tens of thousands, so the only
  // useful card summarises and links out. See the note in FINDING_SAGA_KINDS on
  // why a detector-recognised group is nonetheless not a finding CARD.
  "spam.dust_campaign"
]);
var CUSTODY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "flow.exchange_deposit"
]);
function sagaKindsByShape() {
  const known = /* @__PURE__ */ new Set([
    ...Object.keys(SAGA_LABELS),
    ...FINDING_SAGA_KINDS,
    ...POSITION_SAGA_KINDS,
    ...ACTIVITY_SAGA_KINDS,
    ...CUSTODY_SAGA_KINDS
  ]);
  const order = [
    "finding",
    "crossChain",
    "activity",
    "position",
    "custody",
    "linked"
  ];
  const buckets = /* @__PURE__ */ new Map();
  for (const kind of known) {
    const category = groupCardCategory(kind, null);
    const list = buckets.get(category) ?? [];
    list.push(kind);
    buckets.set(category, list);
  }
  return order.map((category) => ({
    category,
    title: GROUP_CARD_TITLES[category],
    kinds: (buckets.get(category) ?? []).sort(
      (a, b) => sagaLabel(a).localeCompare(sagaLabel(b))
    )
  })).filter((bucket) => bucket.kinds.length > 0);
}
var GROUP_CARD_TITLES = {
  crossChain: "Cross-chain",
  finding: "Findings",
  position: "Trading position",
  // Addresses too: an authored address-flow group (a wallet set's transfers) is
  // an activity stream, and a heading of pools and tokens misfiled it.
  activity: "Pool, token & address activity",
  custody: "Exchange transfers",
  linked: "Related transactions"
};
var GROUP_SHAPE_LABELS = {
  crossChain: "Cross-chain",
  finding: "Finding",
  position: "Position",
  activity: "Activity",
  custody: "Exchange",
  linked: "Linked"
};
var GROUP_SHAPE_MEANING = {
  crossChain: "Legs of one transfer on different chains, linked by a shared message id.",
  finding: "A detector recognised a pattern here \u2014 membership in this group is itself the finding.",
  position: "One wallet's buys and sells of one token.",
  activity: "Everything that has happened to one pool or token launch.",
  custody: "One wallet's transfers to and from a custodial venue.",
  linked: "Related transactions the engine linked without a more specific shape."
};
function groupShapeLabel(sagaKind, relationshipType) {
  return GROUP_SHAPE_LABELS[groupCardCategory(sagaKind, relationshipType)];
}
function groupShapeMeaning(sagaKind, relationshipType) {
  return GROUP_SHAPE_MEANING[groupCardCategory(sagaKind, relationshipType)];
}
var GROUP_STATE_MEANING = {
  open: "Watching for further legs.",
  partial: "Some legs have arrived; still watching for the rest.",
  completed: "Every expected leg arrived.",
  timed_out: "Stopped waiting for the missing leg. That is a guess about a leg that never arrived, not a verdict \u2014 a reprocess can still reopen this group if the leg turns up.",
  cancelled: "Deliberately closed. This does not reopen.",
  reorged: "A member transaction left the canonical chain. This does not reopen."
};
function groupStateMeaning(state) {
  return GROUP_STATE_MEANING[(state ?? "").trim().toLowerCase()] ?? "";
}
function isReconsiderableState(state) {
  const value = (state ?? "").trim().toLowerCase();
  return value === "open" || value === "partial" || value === "timed_out";
}
function isReopenableState(state) {
  return (state ?? "").trim().toLowerCase() === "timed_out";
}
function groupStateLabel(state) {
  const value = (state ?? "").trim().toLowerCase() || "open";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");
}
function groupCardCategory(sagaKind, relationshipType) {
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (kind) {
    if (FINDING_SAGA_KINDS.has(kind)) return "finding";
    if (POSITION_SAGA_KINDS.has(kind)) return "position";
    if (ACTIVITY_SAGA_KINDS.has(kind)) return "activity";
    if (CUSTODY_SAGA_KINDS.has(kind)) return "custody";
    if (kind.startsWith("bridge.")) return "crossChain";
    if (kind.startsWith("trade.")) return "position";
  }
  const relationship = (relationshipType ?? "").trim().toLowerCase();
  if (relationship === "cross_chain_message" || relationship === "intent_fulfillment") {
    return "crossChain";
  }
  if (relationship === "pool_lifecycle" || relationship === "token_launch" || relationship === "market_lifecycle" || relationship === "dust_campaign" || // Every transaction between addresses a rule named: a stream about one
  // subject, like a pool's history. Mirrors `_RELATIONSHIP_SHAPES`.
  relationship === "address_flow") {
    return "activity";
  }
  if (relationship === "trade") return "position";
  if (relationship === "exchange_deposit") return "custody";
  if (relationship === "jit_liquidity" || relationship === "mev_sandwich" || relationship === "liquidity_rug" || relationship === "insider_buy" || relationship === "peel_chain") {
    return "finding";
  }
  return "linked";
}
function groupTransactionCount(group) {
  const txs = group.transaction_count;
  if (typeof txs === "number" && txs > 0) return txs;
  const members = group.members?.length;
  if (typeof members === "number" && members > 0) return members;
  const legs = group.member_count;
  return typeof legs === "number" && legs > 0 ? legs : 0;
}
function isSoleMemberActivity(group) {
  if (!isOpenEndedGroup(group.saga_kind, group.relationship_type, group.open_ended)) {
    return false;
  }
  return groupTransactionCount(group) <= 1;
}
function compareGroupCards(a, b) {
  const soleA = isSoleMemberActivity(a);
  const soleB = isSoleMemberActivity(b);
  if (soleA !== soleB) return soleA ? 1 : -1;
  const sizeDelta = groupTransactionCount(b) - groupTransactionCount(a);
  if (sizeDelta !== 0) return sizeDelta;
  return (a.saga_kind ?? "").localeCompare(b.saga_kind ?? "");
}
function prettifyToken(value) {
  const words = value.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length === 0 ? value : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function sagaLabel(kind) {
  if (!kind) return "Cross-chain";
  return SAGA_LABELS[kind] ?? prettifyToken(kind);
}
var SAGA_ID_LABELS = {
  message_id: "Message ID",
  vaa_id: "VAA",
  sequence: "Sequence",
  nonce: "Nonce",
  // The tuple keys. A prettify would read "Packet Path" / "Deposit Key", which
  // is close enough to leave alone — but "Pathway" is what LayerZero calls it
  // and "Packet origin" is what the V2 spec's own comment calls the tuple, so
  // spelling them here costs one line and stops the surface inventing a name
  // for something the protocol already named.
  packet_path: "Packet pathway",
  packet_origin: "Packet origin",
  deposit_key: "Deposit",
  transfer_id: "Transfer ID",
  packet_id: "Packet"
};
function sagaIdLabel(idLabel) {
  const key = (idLabel ?? "").trim().toLowerCase();
  if (!key) return "Correlation ID";
  return SAGA_ID_LABELS[key] ?? prettifyToken(key);
}
function titleRole(role) {
  return role.length ? role.charAt(0).toUpperCase() + role.slice(1) : role;
}
function sagaKeyOf(ref) {
  const kind = (ref.sagaKind ?? "").trim().toLowerCase();
  const value = (ref.value ?? "").trim().toLowerCase();
  if (!kind || !value) return null;
  return `${kind}:${value}`;
}
function compareSagas(a, b) {
  if (a.awaiting !== b.awaiting) return a.awaiting ? -1 : 1;
  if (a.legs.length !== b.legs.length) return b.legs.length - a.legs.length;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}
function refsFromGroups(groups) {
  const out = [];
  for (const group of groups) {
    const sagaKind = group.saga_kind?.trim();
    if (!sagaKind || group.open_ended) continue;
    if (groupCardCategory(sagaKind, group.relationship_type) !== "crossChain") continue;
    const value = (group.id_value ?? "").trim() || group.group_id;
    out.push({
      value: value.toLowerCase(),
      sagaKind,
      role: group.role ?? void 0,
      idLabel: group.id_label ?? void 0,
      relationshipType: group.relationship_type ?? void 0,
      linkageModel: group.linkage_model ?? void 0,
      confidence: group.confidence ?? void 0,
      source: "grouping_engine"
    });
  }
  return out;
}
function sagasFromRefs(entries, rowKey) {
  const buckets = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    const key = rowKey(entry.row);
    for (const ref of entry.refs) {
      const sagaKey = sagaKeyOf(ref);
      if (!sagaKey) continue;
      let bucket = buckets.get(sagaKey);
      if (!bucket) {
        const sagaKind = (ref.sagaKind ?? "").trim().toLowerCase();
        bucket = {
          seen: /* @__PURE__ */ new Set(),
          saga: {
            key: sagaKey,
            sagaKind,
            value: (ref.value ?? "").trim().toLowerCase(),
            protocolId: ref.protocolId,
            idLabel: ref.idLabel,
            linkageType: ref.linkageType,
            linkageModel: ref.linkageModel,
            relationshipType: ref.relationshipType,
            legs: [],
            rolesPresent: [],
            expectedRoles: [],
            missingRoles: [],
            awaiting: false,
            complete: false,
            openEnded: isOpenEndedGroup(sagaKind, ref.relationshipType)
          }
        };
        buckets.set(sagaKey, bucket);
      }
      if (!bucket.seen.has(key)) {
        bucket.seen.add(key);
        const role = canonRole(ref.role);
        bucket.saga.legs.push({
          row: entry.row,
          role,
          rawRole: ref.role && ref.role !== role ? ref.role : void 0,
          ref
        });
      }
    }
  }
  for (const { saga } of buckets.values()) {
    saga.legs.sort((a, b) => roleRank(a.role) - roleRank(b.role));
    const declared = saga.legs.map((leg) => leg.ref.completeRoles).find((r) => r && r.length > 0);
    const roles = saga.legs.map((leg) => leg.role);
    const { expected, missing } = expectedMissingRoles(roles, declared);
    saga.rolesPresent = [...new Set(roles.filter(Boolean))];
    saga.expectedRoles = expected;
    saga.missingRoles = saga.openEnded ? [] : missing;
    saga.complete = saga.missingRoles.length === 0;
    saga.awaiting = !saga.openEnded && !saga.complete;
  }
  const sagas = [...buckets.values()].map((b) => b.saga).sort(compareSagas);
  return { sagas, byRow: rowStatesFromSagas(sagas, rowKey) };
}
function hashesOf(group) {
  const out = /* @__PURE__ */ new Set();
  for (const member of group.members ?? []) {
    const hash = (member.tx_hash ?? "").trim().toLowerCase();
    if (hash) out.add(hash);
  }
  return out;
}
function reconcileSagas(sagas, groupsFor, txHashOf) {
  if (sagas.length === 0) return [];
  const out = sagas.map((saga) => {
    const legHashes = new Set(
      saga.legs.map((leg) => (txHashOf(leg.row) ?? "").trim().toLowerCase()).filter(Boolean)
    );
    let best;
    let bestOverlap = 0;
    const seen = /* @__PURE__ */ new Set();
    for (const leg of saga.legs) {
      for (const group of groupsFor(leg.row)) {
        if (seen.has(group.group_id)) continue;
        seen.add(group.group_id);
        if (saga.sagaKind && group.saga_kind && group.saga_kind !== saga.sagaKind) continue;
        let overlap = 0;
        for (const hash of hashesOf(group)) if (legHashes.has(hash)) overlap += 1;
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          best = group;
        }
      }
    }
    if (!best) return saga;
    const missing = [...best.missing_roles ?? []];
    const expected = best.expected_roles?.length ? [...best.expected_roles] : saga.expectedRoles;
    const openEnded = best.open_ended ?? saga.openEnded;
    return {
      ...saga,
      expectedRoles: expected,
      missingRoles: openEnded ? [] : missing,
      awaiting: openEnded ? false : best.awaiting ?? missing.length > 0,
      complete: openEnded || missing.length === 0,
      openEnded,
      confirmed: true,
      groupId: best.group_id,
      groupState: best.state ?? void 0,
      memberCount: best.member_count ?? void 0
    };
  });
  return out.sort(compareSagas);
}
function rowStatesFromSagas(sagas, rowKey) {
  const byRow = /* @__PURE__ */ new Map();
  for (const saga of sagas) {
    for (const leg of saga.legs) {
      const key = rowKey(leg.row);
      const list = byRow.get(key);
      if (list) list.push(saga);
      else byRow.set(key, [saga]);
    }
  }
  const out = /* @__PURE__ */ new Map();
  for (const [key, rowSagas] of byRow) {
    const roles = new Set(
      rowSagas.flatMap((s) => s.legs.filter((l) => rowKey(l.row) === key).map((l) => l.role)).filter(Boolean)
    );
    out.set(key, {
      sagas: rowSagas,
      complete: rowSagas.filter((s) => s.complete).length,
      total: rowSagas.length,
      missingRoles: [...new Set(rowSagas.flatMap((s) => s.missingRoles))],
      awaiting: rowSagas.some((s) => s.awaiting),
      role: roles.size === 1 ? [...roles][0] : void 0,
      sagaKind: rowSagas[0]?.sagaKind,
      protocolId: rowSagas[0]?.protocolId,
      confirmed: rowSagas.every((s) => s.confirmed) ? true : void 0
    });
  }
  return out;
}

// src/chains/ingest-coverage.generated.json
var ingest_coverage_generated_default = {
  ingestable_chain_ids: [
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "bip122:00040fe8ec8471911baa1db1266ea15d",
    "eip155:1",
    "eip155:10",
    "eip155:1101",
    "eip155:11155111",
    "eip155:11155420",
    "eip155:137",
    "eip155:17000",
    "eip155:2020",
    "eip155:2442",
    "eip155:25",
    "eip155:250",
    "eip155:324",
    "eip155:42161",
    "eip155:421614",
    "eip155:43114",
    "eip155:56",
    "eip155:747",
    "eip155:80002",
    "eip155:81457",
    "eip155:8453",
    "eip155:84532",
    "eip155:97",
    "eip155:998",
    "eip155:999",
    "hyperliq:mainnet",
    "hyperliq:testnet",
    "monero:418015bb9ae982a1975da7d79277c270",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1"
  ],
  provider_free_sources: [
    "inline"
  ],
  routes: [
    {
      chain_ids: [
        "eip155:1",
        "eip155:10",
        "eip155:1101",
        "eip155:11155111",
        "eip155:11155420",
        "eip155:137",
        "eip155:17000",
        "eip155:2020",
        "eip155:2442",
        "eip155:25",
        "eip155:250",
        "eip155:324",
        "eip155:42161",
        "eip155:421614",
        "eip155:43114",
        "eip155:56",
        "eip155:747",
        "eip155:80002",
        "eip155:81457",
        "eip155:8453",
        "eip155:84532",
        "eip155:97"
      ],
      family: "evm",
      provider: "moralis",
      source_kinds: [
        "block_range",
        "latest",
        "pair_swaps",
        "token_transfers",
        "tx_hashes",
        "tx_specs",
        "wallet_swaps"
      ]
    },
    {
      chain_ids: [
        "eip155:998",
        "eip155:999"
      ],
      family: "evm",
      provider: "evm_rpc",
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "hyperliq:mainnet",
        "hyperliq:testnet"
      ],
      family: "hypercore",
      provider: "hyperliquid",
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
        "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
        "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1"
      ],
      family: "solana",
      provider: "helius",
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:000000000019d6689c085ae165831e93",
        "bip122:000000000933ea01ad0ee984209779ba"
      ],
      family: "utxo",
      provider: "esplora",
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:00040fe8ec8471911baa1db1266ea15d",
        "monero:418015bb9ae982a1975da7d79277c270"
      ],
      family: "utxo",
      provider: "xmrchain+blockchair_api",
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    }
  ],
  wallet_history_chain_ids: [
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "eip155:1",
    "eip155:10",
    "eip155:1101",
    "eip155:11155111",
    "eip155:11155420",
    "eip155:137",
    "eip155:17000",
    "eip155:2020",
    "eip155:2442",
    "eip155:25",
    "eip155:250",
    "eip155:324",
    "eip155:42161",
    "eip155:421614",
    "eip155:43114",
    "eip155:56",
    "eip155:747",
    "eip155:80002",
    "eip155:81457",
    "eip155:8453",
    "eip155:84532",
    "eip155:97",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1"
  ]
};

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2 ?? chain.chainIdHex ?? chain.slug;
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
function chainDisplayName(value) {
  if (!value) return "";
  return getChainMetadata(value)?.name ?? value;
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical(target) {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      target.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (target.get(slug)) continue;
    try {
      target.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
var registry = globalSingleton("chains/registry", () => {
  const seeded = new ChainRegistry();
  seedFromCanonical(seeded);
  return seeded;
});

// src/chains/coverage.ts
var RouteSchema = z.object({
  family: z.string().default(""),
  provider: z.string().default(""),
  source_kinds: z.array(z.string()).default([]),
  chain_ids: z.array(z.string()).default([])
});
var CoverageSchema = z.object({
  wallet_history_chain_ids: z.array(z.string()).default([]),
  ingestable_chain_ids: z.array(z.string()).default([]),
  provider_free_sources: z.array(z.string()).default([]),
  routes: z.array(RouteSchema).default([])
});
var SNAPSHOT = CoverageSchema.parse(ingest_coverage_generated_default);
Object.freeze(SNAPSHOT.wallet_history_chain_ids);
var INGEST_ROUTES = Object.freeze(
  SNAPSHOT.routes.map(
    (route) => Object.freeze({
      family: route.family,
      provider: route.provider,
      sourceKinds: Object.freeze([...route.source_kinds]),
      chainIds: Object.freeze([...route.chain_ids])
    })
  )
);
var PROVIDER_FREE_SOURCES = Object.freeze(
  SNAPSHOT.provider_free_sources
);
var PROVIDER_FREE = new Set(PROVIDER_FREE_SOURCES);
var KINDS_BY_CHAIN = (() => {
  const out = /* @__PURE__ */ new Map();
  for (const route of INGEST_ROUTES) {
    for (const chainId of route.chainIds) {
      const kinds = out.get(chainId) ?? /* @__PURE__ */ new Set();
      for (const kind of route.sourceKinds) kinds.add(kind);
      out.set(chainId, kinds);
    }
  }
  return out;
})();
Object.freeze(
  SNAPSHOT.ingestable_chain_ids
);
function canonicalOf(chainId) {
  return toCanonicalChainId(chainId) ?? chainId;
}
function isIngestableChain(chainId, sourceKind) {
  if (!chainId) return false;
  const kinds = KINDS_BY_CHAIN.get(canonicalOf(chainId));
  return PROVIDER_FREE.has(sourceKind) || (kinds?.has(sourceKind) ?? false);
}

// src/funds/moves.ts
var VALUE_SCENE_KINDS = Object.freeze(["flow", "interaction"]);
var STRUCTURAL_EDGE_KINDS = /* @__PURE__ */ new Set(["call", "contains", "creates", "verify"]);
function num(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function lower(value) {
  return value ? value.toLowerCase() : null;
}
function partyOf(node, fallbackId) {
  return lower(node?.address) ?? fallbackId;
}
function assetOf(edge, txChain) {
  const a = edge.asset;
  if (!a) return null;
  const chainId = a.token_chain_id ?? edge.from_chain ?? txChain ?? null;
  const tokenAddress = lower(a.token_address);
  return {
    key: `${chainId ?? ""}:${tokenAddress ?? "native"}`,
    symbol: a.symbol ?? null,
    chainId,
    tokenAddress,
    decimals: a.decimals ?? null
  };
}
function amountOf(edge) {
  const a = edge.asset;
  if (!a) return null;
  const display = num(a.amount);
  if (display !== null) return display;
  const raw = num(a.amount_raw);
  if (raw === null || a.decimals == null) return null;
  return raw / 10 ** a.decimals;
}
function isObserved(edge) {
  return (edge.origin ?? "decoded") === "decoded" && !edge.virtual;
}
function isValueScene(scene) {
  return !!scene && VALUE_SCENE_KINDS.includes(String(scene.kind));
}
function movesFromScene(scene, txId, txChain) {
  if (!isValueScene(scene)) return [];
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const out = [];
  for (const edge of scene.edges) {
    if (!isObserved(edge) || STRUCTURAL_EDGE_KINDS.has(String(edge.kind))) continue;
    const asset = assetOf(edge, txChain);
    if (!asset) continue;
    const amount = amountOf(edge);
    if (amount === null || amount === 0) continue;
    const from = partyOf(nodes.get(edge.source), edge.source);
    const to = partyOf(nodes.get(edge.target), edge.target);
    if (from === to) continue;
    out.push({
      txId,
      chainId: edge.from_chain ?? nodes.get(edge.source)?.chain_id ?? txChain ?? null,
      from,
      to,
      asset,
      amount,
      usd: num(edge.asset?.usd)
    });
  }
  return out;
}
function chainOf(node, edgeChain) {
  return node?.chain_id ?? edgeChain ?? null;
}
function bridgeHopsFromScene(scene, txId, txChain) {
  if (!scene) return [];
  const local = txChain;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const isLocal = (chain) => !!chain && !!local && chain === local;
  const isRemote = (chain) => !!chain && (!local || chain !== local);
  const hops = /* @__PURE__ */ new Map();
  const add = (hop) => {
    const key = `${hop.direction}|${hop.localParty ?? ""}|${hop.remoteChain ?? ""}`;
    if (!hops.has(key)) hops.set(key, hop);
  };
  for (const bridge of scene.nodes) {
    if (!bridge.off_chain) continue;
    const into = scene.edges.filter((e) => e.target === bridge.id);
    const outOf = scene.edges.filter((e) => e.source === bridge.id);
    for (const e1 of into) {
      const srcNode = nodes.get(e1.source);
      const srcChain = chainOf(srcNode, e1.from_chain);
      for (const e2 of outOf) {
        const dstNode = nodes.get(e2.target);
        const dstChain = chainOf(dstNode, e2.to_chain);
        const valueEdge = e1.asset ? e1 : e2;
        const base = {
          txId,
          localChain: local,
          protocol: bridge.label || null,
          asset: assetOf(valueEdge, local),
          amount: amountOf(valueEdge),
          usd: num(valueEdge.asset?.usd)
        };
        if (isLocal(srcChain) && isRemote(dstChain)) {
          add({
            ...base,
            direction: "out",
            remoteChain: dstChain,
            localParty: srcNode?.address ? lower(srcNode.address) : null,
            remoteParty: lower(dstNode?.address),
            expected: !isObserved(e2)
          });
        } else if (isRemote(srcChain) && isLocal(dstChain)) {
          add({
            ...base,
            direction: "in",
            remoteChain: srcChain,
            localParty: dstNode?.address ? lower(dstNode.address) : null,
            remoteParty: lower(srcNode?.address),
            expected: !isObserved(e1)
          });
        }
      }
    }
  }
  for (const edge of scene.edges) {
    if (!edge.cross_chain) continue;
    const from = edge.from_chain ?? null;
    const to = edge.to_chain ?? null;
    const src = nodes.get(edge.source);
    const dst = nodes.get(edge.target);
    if (src?.off_chain || dst?.off_chain) continue;
    const base = {
      txId,
      localChain: local,
      protocol: null,
      asset: assetOf(edge, local),
      amount: amountOf(edge),
      usd: num(edge.asset?.usd),
      expected: !isObserved(edge)
    };
    if (isLocal(from) && isRemote(to)) {
      add({ ...base, direction: "out", remoteChain: to, localParty: lower(src?.address), remoteParty: lower(dst?.address) });
    } else if (isRemote(from) && isLocal(to)) {
      add({ ...base, direction: "in", remoteChain: from, localParty: lower(dst?.address), remoteParty: lower(src?.address) });
    }
  }
  return [...hops.values()];
}
function readTransactionMoney(input) {
  const moves = movesFromScene(input.valueScene, input.txId, input.chainId);
  let hops = bridgeHopsFromScene(input.valueScene, input.txId, input.chainId);
  if (hops.length === 0 && input.semanticScene) {
    hops = bridgeHopsFromScene(input.semanticScene, input.txId, input.chainId);
  }
  return { moves, hops, hasValueScene: isValueScene(input.valueScene) };
}

// src/grouping/reach.ts
function expectedRemoteChains(legs) {
  const present = /* @__PURE__ */ new Set();
  for (const leg of legs) {
    const chain = toCanonicalChainId(leg.txChain);
    if (chain) present.add(chain);
  }
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const leg of legs) {
    const { hops } = readTransactionMoney({
      txId: leg.txId,
      chainId: leg.txChain,
      valueScene: leg.scene,
      semanticScene: leg.semanticScene ?? null
    });
    for (const hop of hops) {
      const chain = toCanonicalChainId(hop.remoteChain);
      if (!chain || present.has(chain) || seen.has(chain)) continue;
      seen.add(chain);
      out.push(chain);
    }
  }
  return out;
}
var LEG_SOURCE_KIND = "tx_hashes";
var UNKNOWN_REACH = Object.freeze({ kind: "unknown", chain: null });
function legReach(chains) {
  const uncovered = chains.find((chain) => chain && !isIngestableChain(chain, LEG_SOURCE_KIND));
  if (uncovered) return { kind: "uncovered", chain: toCanonicalChainId(uncovered) ?? uncovered };
  const first = chains.find(Boolean);
  if (first) return { kind: "fetchable", chain: toCanonicalChainId(first) ?? first };
  return UNKNOWN_REACH;
}
function missingLegReach(legs) {
  return legReach(expectedRemoteChains(legs));
}
function missingLegReason(reach, { confirmed = false } = {}) {
  const where = reach.chain ? chainDisplayName(reach.chain) : "";
  switch (reach.kind) {
    case "uncovered":
      return `is expected on ${where}, which no configured provider indexes \u2014 a run cannot fetch it, even by hash. Paste it with the run dialog's Fixed payloads source.`;
    case "fetchable":
      return confirmed ? `has not arrived on ${where}` : `is expected on ${where} \u2014 add it by hash or ingest its address on the Scope tab, then pin it here`;
    default:
      return confirmed ? "has not arrived" : "not among this case's transactions";
  }
}
function missingLegLabel(reach, options = {}) {
  const where = reach.chain ? chainDisplayName(reach.chain) : "";
  if (reach.kind === "uncovered") return `${where} not indexed`;
  if (reach.kind === "fetchable") return options.confirmed ? `awaiting ${where}` : `not in this set (${where})`;
  return options.confirmed ? "awaiting" : "not in this set";
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  PERM002: spec("PERM002", "Resource access denied", "You do not have access to this resource within your tenant.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
(class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
});

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  static {
    brandClass(this, "api/ApiError");
  }
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/api/wire.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function rowsFromWire(data) {
  const rows = Array.isArray(data) ? data : isRecord(data) ? data.items : void 0;
  return Array.isArray(rows) ? rows.filter(isRecord) : [];
}

// src/grouping/reads.ts
var GROUP_PAGE_SIZE = 100;
var MAX_GROUP_PAGES = 5;
async function walkGroups(client, { sagaKind, scopeParams, signal, resource = "groups", maxPages = MAX_GROUP_PAGES } = {}) {
  const rows = [];
  let cursor = null;
  for (let page = 0; page < maxPages; page += 1) {
    const query = {
      ...scopeParams ?? {},
      limit: String(GROUP_PAGE_SIZE),
      ...sagaKind ? { saga_kind: sagaKind } : {},
      ...cursor ? { cursor } : {}
    };
    const payload = unwrapData(await client.get(`/grouping/${resource}`, { query, signal }));
    rows.push(...rowsFromWire(payload));
    const next = isRecord(payload) ? payload.next_cursor : null;
    cursor = typeof next === "string" && next ? next : null;
    if (!cursor) return { rows: collapseGroupPages(rows), truncated: false };
  }
  return { rows: collapseGroupPages(rows), truncated: true };
}
function collapseGroupPages(rows) {
  const merged = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = row.saga_key ?? row.id ?? row.group_id ?? row;
    const previous = merged.get(key);
    if (!previous || Number(row.read_precedence ?? -1) > Number(previous.read_precedence ?? -1)) merged.set(key, row);
  }
  return [...merged.values()];
}

export { ACTIVITY_SAGA_KINDS, CANON_DESTINATION, CANON_SOURCE, CUSTODY_SAGA_KINDS, DEFAULT_COMPLETE_ROLES, FINDING_SAGA_KINDS, GROUPING_COMPARATORS, GROUPING_SCOPES, GROUP_CARD_TITLES, GROUP_PAGE_SIZE, GROUP_RELATIONSHIP_LABELS, GROUP_SHAPE_LABELS, GROUP_SHAPE_MEANING, GROUP_STATE_MEANING, GroupingRuleSchema, LEG_SOURCE_KIND, MAX_GROUP_PAGES, OPEN_ENDED_RELATIONSHIP_TYPES, OPEN_ENDED_SAGA_KINDS, OPEN_ENDED_SAGA_PREFIXES, POSITION_SAGA_KINDS, ROLE_COMPLEMENT, ROLE_LABELS, ROLE_RANK, SAGA_ID_LABELS, SAGA_LABELS, UNKNOWN_ROLE_RANK, approveThenSendRule, canonRole, collapseGroupPages, compareGroupCards, compareSagas, evaluateRule, evaluateRules, expectedMissingRoles, expectedRemoteChains, groupCardCategory, groupRelationshipLabel, groupShapeLabel, groupShapeMeaning, groupStateLabel, groupStateMeaning, groupTransactionCount, isFindingSaga, isOpenEndedGroup, isReconsiderableState, isReopenableState, isSoleMemberActivity, legReach, missingLegLabel, missingLegReach, missingLegReason, peelChainLabel, presentRoles, prettifyToken, reconcileSagas, refsFromGroups, roleLabel, roleRank, rowStatesFromSagas, sagaIdLabel, sagaKindsByShape, sagaLabel, sagasFromRefs, stepMatches, titleRole, validateGroupingRule, walkGroups };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map